/**
 * 商品詳細の画像ギャラリー（#56）
 *
 * 画像は縦1列に並べ、ページのスクロールで1枚ずつ見せる。
 * 画像をクリック（Enter / Space）すると拡大オーバーレイを開く。
 * 閉じるのは「閉じるボタン」「画像の外側」「Esc」の3つ。
 * 拡大中は左右の矢印キー・画面左右のボタン・横スワイプで前後の画像へ移動する（#94(4)）。
 */
(() => {
  'use strict';

  const gallery = document.querySelector('[data-stilllife-product-gallery]');
  const lightbox = document.querySelector('[data-stilllife-product-lightbox]');
  if (!gallery || !lightbox) return;

  const slides = Array.from(gallery.querySelectorAll('[data-stilllife-product-slide]'));
  if (slides.length === 0) return;

  const image = lightbox.querySelector('[data-stilllife-lightbox-image]');
  const closeButton = lightbox.querySelector('[data-stilllife-lightbox-close]');
  const previousButton = lightbox.querySelector('[data-stilllife-lightbox-previous]');
  const nextButton = lightbox.querySelector('[data-stilllife-lightbox-next]');
  if (!image || !closeButton) return;

  let currentIndex = 0;
  let lastFocused = null;

  const focusables = () =>
    Array.from(lightbox.querySelectorAll('button')).filter((element) => !element.hidden);

  const show = (index) => {
    const slide = slides[(index + slides.length) % slides.length];
    currentIndex = Number(slide.dataset.index || 0);
    image.src = slide.dataset.full || '';
    image.alt = slide.dataset.alt || '';
  };

  const open = (slide) => {
    lastFocused = slide;
    show(Number(slide.dataset.index || 0));
    lightbox.hidden = false;
    document.body.classList.add('is-lightbox-open');
    closeButton.focus();
  };

  const close = () => {
    lightbox.hidden = true;
    document.body.classList.remove('is-lightbox-open');
    // 画像のsrcは残さない（拡大用の2000px画像を保持し続けないため）
    image.src = '';
    if (lastFocused) lastFocused.focus();
    lastFocused = null;
  };

  const move = (increment) => show(currentIndex + increment);

  slides.forEach((slide) => {
    slide.addEventListener('click', () => open(slide));
  });

  closeButton.addEventListener('click', close);
  if (previousButton) previousButton.addEventListener('click', () => move(-1));
  if (nextButton) nextButton.addEventListener('click', () => move(1));

  /* 横スワイプで前後へ送る（#94(4)）。
     SP のギャラリー本体（#88）は CSS の scroll-snap だが、ライトボックスは img を
     差し替える作りでスクロールコンテナが無いため、ここだけ指の動きを直接見る。

     Pointer Events ではなく Touch Events を使う。横に払うとブラウザがスクロール
     ジェスチャと判定してポインタを pointercancel し、pointerup が飛んでこないため
     （2026-09-24 実測: pointerdown → pointermove 1回 → touchend で、pointerup なし）。
     touchend は同じ操作でも必ず飛ぶ。

     - 指が2本以上になったらピンチズームとみなして取り消す
     - 横の移動量が 40px 以上、かつ縦の 1.5 倍を超えたときだけ送る（縦スクロール・
       ピンチと取り違えない）
     - preventDefault はしない（ピンチズームとスクロールを殺さないため） */
  const SWIPE_MIN_X = 40;
  const SWIPE_RATIO = 1.5;
  let swipeStart = null;
  let swipeHandled = false;

  // 画像の外側（オーバーレイ自身）のクリックで閉じる。
  // スワイプで送った直後の click は閉じない（上の swipeHandled を見る）
  lightbox.addEventListener('click', (event) => {
    if (swipeHandled) return;
    if (event.target === lightbox || event.target.classList.contains('p-product__lightbox-figure')) {
      close();
    }
  });

  lightbox.addEventListener(
    'touchstart',
    (event) => {
      if (slides.length < 2 || event.touches.length > 1) {
        swipeStart = null;
        return;
      }
      const touch = event.changedTouches[0];
      swipeStart = { x: touch.clientX, y: touch.clientY, id: touch.identifier };
    },
    { passive: true }
  );

  lightbox.addEventListener(
    'touchmove',
    (event) => {
      // 途中で2本目の指が乗ったらピンチ。送らない
      if (event.touches.length > 1) swipeStart = null;
    },
    { passive: true }
  );

  lightbox.addEventListener(
    'touchend',
    (event) => {
      if (!swipeStart) return;

      const touch = Array.from(event.changedTouches).find(
        (item) => item.identifier === swipeStart.id
      );
      if (!touch) return;

      const dx = touch.clientX - swipeStart.x;
      const dy = touch.clientY - swipeStart.y;
      swipeStart = null;

      if (Math.abs(dx) < SWIPE_MIN_X || Math.abs(dx) < Math.abs(dy) * SWIPE_RATIO) return;

      // 左へ払う（dx < 0）と次の画像。紙をめくる向きに合わせる
      move(dx < 0 ? 1 : -1);

      // この直後に飛んでくる click でオーバーレイが閉じないようにする
      swipeHandled = true;
      window.setTimeout(() => {
        swipeHandled = false;
      }, 0);
    },
    { passive: true }
  );

  lightbox.addEventListener('touchcancel', () => {
    swipeStart = null;
  });

  document.addEventListener('keydown', (event) => {
    if (lightbox.hidden) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      close();
      return;
    }

    if (slides.length > 1 && (event.key === 'ArrowLeft' || event.key === 'ArrowRight')) {
      event.preventDefault();
      move(event.key === 'ArrowLeft' ? -1 : 1);
      return;
    }

    // オーバーレイの中にフォーカスを閉じ込める
    if (event.key === 'Tab') {
      const items = focusables();
      if (items.length === 0) return;

      const first = items[0];
      const last = items[items.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  });
})();
